import React from "react";
import styled, { css } from "styled-components";

import { Route } from "src/shared/constants/route";
import { useGetFile } from "src/shared/hooks";
import { Nullable } from "src/shared/utils/typescript";
import { CircleCheckmarkIcon, ClockIcon, XMarkIcon } from "src/static/icons";
import { HackerDirectoryProfilePlaceholder } from "src/static/img";
import { useGetDiscoveryProfileByClaimIdQuery } from "src/views/hacker-directory/graphql/getDiscoveryProfileByClaimId.generated";
import {
  getTeammateRelationship,
  mapDiscoveryProfile,
} from "src/views/hacker-directory/shared/utils";

import { getRelationshipCopy } from "./relationshipCopy";

export interface ProfileTagProps extends React.ComponentPropsWithoutRef<"div"> {
  /** Discovery-profile claim ID for the teammate to display. */
  claimId: number;
  /**
   * Optional display name. When set without relationship props, skips the
   * profile fetch.
   */
  name?: string;
  /** Subject claim ID for mutual-assignment (e.g. auth or viewed profile). */
  authenticatedClaimId?: Nullable<number>;
  /** Subject's selected teammate claim IDs. */
  authenticatedTeammateClaimIds?: Nullable<readonly string[]>;
  /** Name of the profile owner whose teammate is being displayed. */
  profileOwnerName?: Nullable<string>;
  /** Whether the profile owner is the authenticated hacker. */
  isOwnProfile?: boolean;
  /** When provided, shows the remove control. */
  onRemove?: () => void;
  /**
   * On narrow viewports, switch from a tooltip card grid tile to a full-width
   * row with the relationship explanation shown inline.
   */
  inlineStatusOnNarrowScreens?: boolean;
}

const PhotoFrame = styled.span`
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 8px 8px 0 0;
  background: #eff8fa;
`;

const Photo = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TextStack = styled.span`
  display: flex;
  min-width: 0;
  flex-direction: column;
`;

const Label = styled.span`
  display: block;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  padding: 8px 6px 9px;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.3;
  letter-spacing: 0;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const InlineStatus = styled.span<{ $isPending: boolean }>`
  display: none;
  min-width: 0;
  margin: 0;
  padding: 0;
  color: ${({ $isPending, theme }) =>
    $isPending
      ? theme.globalConstants.color.orangePrimary
      : theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const RelationshipIcon = styled.span`
  position: absolute;
  right: 6px;
  bottom: 6px;
  display: flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 1px 3px rgb(0 0 0 / 20%);
`;

const MutualIcon = styled(CircleCheckmarkIcon)`
  display: block;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
`;

const PendingIcon = styled(ClockIcon)`
  display: block;
  flex-shrink: 0;
  width: 17px;
  height: 17px;
  color: ${({ theme }) => theme.globalConstants.color.orangePrimary};
`;

const ProfileLink = styled.a`
  display: flex;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  flex-direction: column;
  border-radius: 8px;
  color: inherit;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.globalConstants.color.borderFocus};
    outline-offset: 3px;
  }
`;

const UnavailableProfile = styled.span`
  display: flex;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  flex-direction: column;
  border-radius: 8px;
`;

const RemoveButton = styled.button`
  position: absolute;
  z-index: 2;
  top: 5px;
  right: 5px;
  display: flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 50%;
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 1px 3px rgb(0 0 0 / 18%);
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.globalConstants.color.borderFocus};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const RelationshipTooltip = styled.span<{
  $isPending: boolean;
  $hideOnNarrow: boolean;
}>`
  position: absolute;
  z-index: 1001;
  bottom: calc(100% + 8px);
  left: 50%;
  box-sizing: border-box;
  width: 230px;
  max-width: calc(100vw - 48px);
  padding: 8px 12px;
  transform: translateX(-50%) translateY(2px);
  border: 1px solid
    ${({ $isPending, theme }) =>
      $isPending
        ? theme.globalConstants.color.orangePrimary
        : theme.globalConstants.color.borderGray};
  border-radius: 8px;
  visibility: hidden;
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 1px 2px rgb(0 0 0 / 6%), 0 1px 3px rgb(0 0 0 / 10%);
  color: ${({ $isPending, theme }) =>
    $isPending
      ? theme.globalConstants.color.orangePrimary
      : theme.globalConstants.color.textPrimary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease, transform 120ms ease, visibility 120ms ease;

  &::before,
  &::after {
    position: absolute;
    top: 100%;
    left: 50%;
    width: 0;
    height: 0;
    transform: translateX(-50%);
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
    content: "";
  }

  &::before {
    border-top: 7px solid
      ${({ $isPending, theme }) =>
        $isPending
          ? theme.globalConstants.color.orangePrimary
          : theme.globalConstants.color.borderGray};
  }

  &::after {
    margin-top: -1px;
    border-top: 6px solid ${({ theme }) => theme.globalConstants.color.white};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${({ $hideOnNarrow, theme }) =>
    $hideOnNarrow &&
    theme.mediaQueries.tablet`
      display: none;
    `}
`;

const CloseIcon = styled(XMarkIcon)`
  display: block;
  width: 16px;
  height: 16px;

  path {
    stroke: currentColor;
  }
`;

const narrowRowStyles = css`
  ${({ theme }) => theme.mediaQueries.tablet`
    width: 100%;
    border-radius: 10px;

    &:hover {
      transform: none;
      box-shadow: none;
    }

    &:hover > [role="tooltip"],
    &:focus-within > [role="tooltip"] {
      visibility: hidden;
      opacity: 0;
    }

    ${ProfileLink}, ${UnavailableProfile} {
      flex-direction: row;
      align-items: center;
      gap: 12px;
      padding: 10px 12px;
      overflow: hidden;
    }

    ${PhotoFrame} {
      width: 56px;
      flex: 0 0 56px;
      aspect-ratio: 1;
      border-radius: 8px;
    }

    ${RelationshipIcon} {
      right: 4px;
      bottom: 4px;
      width: 20px;
      height: 20px;
    }

    ${MutualIcon}, ${PendingIcon} {
      width: 14px;
      height: 14px;
    }

    ${TextStack} {
      flex: 1 1 auto;
      gap: 4px;
      padding-right: 4px;
    }

    ${Label} {
      padding: 0;
      text-align: left;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    ${InlineStatus} {
      display: block;
    }
  `}
`;

const Tag = styled.div<{ $inlineStatusOnNarrowScreens: boolean }>`
  position: relative;
  width: 104px;
  min-width: 0;
  overflow: visible;
  border: 2px solid ${({ theme }) => theme.globalConstants.color.textPrimary};
  border-radius: 10px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  transition: box-shadow 120ms ease, transform 120ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgb(0 0 0 / 12%);
  }

  &:hover > [role="tooltip"],
  &:focus-within > [role="tooltip"] {
    transform: translateX(-50%) translateY(0);
    visibility: visible;
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${({ $inlineStatusOnNarrowScreens }) =>
    $inlineStatusOnNarrowScreens && narrowRowStyles}
`;

/**
 * Reusable compact card for a discovery-profile teammate.
 * Fetches profile by claim ID unless `name` is provided without relationship
 * props; shows the relationship status and explanation when available.
 */
const ProfileTag: React.FC<ProfileTagProps> = ({
  claimId,
  name: nameOverride,
  authenticatedClaimId = null,
  authenticatedTeammateClaimIds = null,
  profileOwnerName = null,
  isOwnProfile = false,
  onRemove,
  inlineStatusOnNarrowScreens = false,
  ...props
}) => {
  const trimmedNameOverride = nameOverride?.trim() ?? "";
  const hasNameOverride = trimmedNameOverride !== "";
  const needsRelationship =
    authenticatedClaimId != null || authenticatedTeammateClaimIds != null;
  const { data, loading } = useGetDiscoveryProfileByClaimIdQuery({
    variables: { claimId },
    skip:
      (hasNameOverride && !needsRelationship) ||
      !Number.isInteger(claimId) ||
      claimId <= 0,
  });

  const mapped = data?.profiles[0]
    ? mapDiscoveryProfile(claimId, data.profiles[0].fields)
    : null;
  const photo = useGetFile(mapped?.profilePhoto ?? null);
  const name = hasNameOverride ? trimmedNameOverride : mapped?.name ?? null;
  const label = !hasNameOverride && loading ? "…" : name?.trim() || "Unknown";
  const relationship = getTeammateRelationship({
    authenticatedClaimId,
    authenticatedTeammateClaimIds,
    targetClaimId: claimId,
    targetTeammateClaimIds: mapped?.teammatesClaimIds ?? null,
  });
  const relationshipCopy = getRelationshipCopy({
    relationship,
    profileOwnerName,
    teammateName: label,
    isOwnProfile,
  });
  const relationshipTooltipId = `profile-tag-relationship-${
    authenticatedClaimId ?? "unknown"
  }-${claimId}`;
  const profileHref = mapped
    ? `${Route.HACKER_DIRECTORY_PROFILE}?ref=${claimId}`
    : null;

  const content = (
    <>
      <PhotoFrame>
        {photo?.uri ? (
          <Photo
            src={photo.uri}
            alt=""
            aria-hidden
            loading="lazy"
            decoding="async"
          />
        ) : (
          <Photo src={HackerDirectoryProfilePlaceholder} alt="" aria-hidden />
        )}
        {relationship === "mutual" && (
          <RelationshipIcon>
            <MutualIcon aria-hidden />
          </RelationshipIcon>
        )}
        {relationship === "pending" && (
          <RelationshipIcon>
            <PendingIcon aria-hidden />
          </RelationshipIcon>
        )}
      </PhotoFrame>
      <TextStack>
        <Label title={label}>{label}</Label>
        {relationshipCopy != null && (
          <InlineStatus $isPending={relationship === "pending"}>
            {relationshipCopy}
          </InlineStatus>
        )}
      </TextStack>
    </>
  );

  return (
    <Tag
      {...props}
      $inlineStatusOnNarrowScreens={inlineStatusOnNarrowScreens}
      aria-label={
        relationshipCopy == null ? label : `${label}, ${relationshipCopy}`
      }
    >
      {profileHref ? (
        <ProfileLink
          href={profileHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-describedby={
            relationshipCopy == null || inlineStatusOnNarrowScreens
              ? undefined
              : relationshipTooltipId
          }
        >
          {content}
        </ProfileLink>
      ) : (
        <UnavailableProfile>{content}</UnavailableProfile>
      )}
      {onRemove != null && (
        <RemoveButton
          type="button"
          aria-label={`Remove ${label}`}
          onClick={onRemove}
        >
          <CloseIcon aria-hidden />
        </RemoveButton>
      )}
      {relationshipCopy != null && (
        <RelationshipTooltip
          id={relationshipTooltipId}
          role="tooltip"
          $isPending={relationship === "pending"}
          $hideOnNarrow={inlineStatusOnNarrowScreens}
        >
          {relationshipCopy}
        </RelationshipTooltip>
      )}
    </Tag>
  );
};

export default ProfileTag;
